import axiosInstance from "../axiosInstance";
import { formatCurrency } from "./currency";
import { getValidUserId } from "./uuid";

// Communities whose default written language is unambiguous. Used to pick the
// site language when the user has no explicit preference (no ?lang param and no
// saved preferred_language) — e.g. a Taiwan SoFest attendee whose phone UI is
// in English would otherwise fall through to navigator.language and get the
// English site, even though the event content is Traditional Chinese. Only list
// single-dominant-language markets here; multilingual markets (sg, my, id)
// intentionally fall back to the browser language.
export const communityToLanguageMap = {
  tw: "zh-Hant-TW", // Taiwan — Traditional Chinese
  hk: "zh-Hant-TW", // Hong Kong — Traditional Chinese
  mo: "zh-Hant-TW", // Macau — Traditional Chinese
  cn: "zh-Hans-CN", // Mainland China — Simplified Chinese
};

// languages supported in locize
export const communityToCurrencyMap = {
  sg: "sgd",
  my: "myr",
  tw: "twd",
  id: "idr",
  th: "thb",
  vn: "vnd",
  ph: "php",
  kr: "krw",
  jp: "jpy",
  hk: "hkd",
  cn: "cny",
  au: "aud",
  nz: "nzd",
  gb: "gbp",
  eu: "eur",
  ca: "cad",
  us: "usd",
  ch: "chf",
  ae: "aed",
  in: "inr",
  br: "brl",
  ru: "rub",
  za: "zar",
  mx: "mxn",
  ar: "ars",
  cl: "clp",
  co: "cop",
  pe: "pen",
  eg: "egp",
  ng: "ngn",
  ke: "kes",
  il: "ils",
  sa: "sar",
  qa: "qar",
  bh: "bhd",
  kw: "kwd",
  om: "omr",
  pk: "pkr",
  bd: "bdt",
  np: "npr",
  lk: "lkr",
  mm: "mmk", // Myanmar (Burma)
  kh: "khr", // Cambodia
  la: "lak", // Laos
  bn: "bnd", // Brunei
  mo: "mop", // Macau
  mn: "mnt", // Mongolia
  uz: "uzs", // Uzbekistan
  kz: "kzt", // Kazakhstan
  kg: "kgs", // Kyrgyzstan
  tj: "tjs", // Tajikistan
  tm: "tmt", // Turkmenistan
  af: "afn", // Afghanistan
  fr: "eur",
  de: "eur",
  it: "eur",
  es: "eur",
  pt: "eur",
  nl: "eur",
  be: "eur",
  at: "eur",
  fi: "eur",
  gr: "eur",
  ie: "eur",
  no: "nok",
  se: "sek",
  dk: "dkk",
  pl: "pln",
  cz: "czk",
  hu: "huf",
  tr: "try",
  ua: "uah",
  is: "isk",
  ro: "ron",
  bg: "bgn",
  hr: "hrk",
  rs: "rsd",
  si: "eur",
  sk: "eur",
  lt: "eur",
  lv: "eur",
  ee: "eur",
  cy: "eur",
  mt: "eur",
  lu: "eur",
  ma: "mad",
  dz: "dzd",
  tn: "tnd",
  ly: "lyd",
};

export const currencyToCreditsGiftMap = {
  sgd: {
    survey: 5,
    welcome: 20,
    welcome_promo: 150,
    first: 10,
    second: 20,
    third: 40,
  },
  myr: {
    survey: 10,
    welcome: 60,
    welcome_promo: 450,
    first: 30,
    second: 60,
    third: 120,
  },
  twd: {
    survey: 100,
    welcome: 500,
    welcome_promo: 4000,
    first: 250,
    second: 500,
    third: 1000,
  },
  idr: {
    survey: 50000,
    welcome: 200000,
    first: 100000,
    second: 200000,
    third: 400000,
  },
  thb: {
    survey: 100,
    welcome: 500,
    first: 250,
    second: 500,
    third: 1000,
  },
  vnd: {
    survey: 80000,
    welcome: 320000,
    first: 160000,
    second: 320000,
    third: 640000,
  },
  php: {
    survey: 200,
    welcome: 800,
    first: 400,
    second: 800,
    third: 1600,
  },
  krw: {
    survey: 5000,
    welcome: 20000,
    first: 10000,
    second: 20000,
    third: 40000,
  },
  jpy: {
    survey: 500,
    welcome: 2000,
    first: 1000,
    second: 2000,
    third: 4000,
  },
  hkd: {
    survey: 30,
    welcome: 120,
    welcome_promo: 1000,
    first: 60,
    second: 120,
    third: 240,
  },
  cny: {
    survey: 25,
    welcome: 100,
    first: 50,
    second: 100,
    third: 200,
  },
  aud: {
    survey: 5,
    welcome: 20,
    first: 10,
    second: 20,
    third: 40,
  },
  nzd: {
    survey: 5,
    welcome: 20,
    first: 10,
    second: 20,
    third: 40,
  },
  gbp: {
    survey: 5,
    welcome: 15,
    first: 10,
    second: 15,
    third: 30,
  },
  eur: {
    survey: 5,
    welcome: 15,
    first: 10,
    second: 15,
    third: 30,
  },
  cad: {
    survey: 5,
    welcome: 20,
    first: 10,
    second: 20,
    third: 40,
  },
  usd: {
    survey: 5,
    welcome: 15,
    first: 10,
    second: 15,
    third: 30,
  },
  chf: {
    survey: 5,
    welcome: 15,
    first: 10,
    second: 15,
    third: 30,
  },
  aed: {
    survey: 10,
    welcome: 50,
    first: 25,
    second: 50,
    third: 100,
  },
  inr: {
    survey: 250,
    welcome: 1000,
    first: 500,
    second: 1000,
    third: 2000,
  },
  brl: {
    survey: 15,
    welcome: 60,
    first: 30,
    second: 60,
    third: 120,
  },
  rub: {
    survey: 300,
    welcome: 1200,
    first: 600,
    second: 1200,
    third: 2400,
  },
  zar: {
    survey: 50,
    welcome: 200,
    first: 100,
    second: 200,
    third: 400,
  },
  mxn: {
    survey: 50,
    welcome: 200,
    first: 100,
    second: 200,
    third: 400,
  },
  ars: {
    survey: 1000,
    welcome: 4000,
    first: 2000,
    second: 4000,
    third: 8000,
  },
  clp: {
    survey: 2500,
    welcome: 10000,
    first: 5000,
    second: 10000,
    third: 20000,
  },
  cop: {
    survey: 10000,
    welcome: 40000,
    first: 20000,
    second: 40000,
    third: 80000,
  },
  pen: {
    survey: 10,
    welcome: 40,
    first: 20,
    second: 40,
    third: 80,
  },
  egp: {
    survey: 100,
    welcome: 400,
    first: 200,
    second: 400,
    third: 800,
  },
  ngn: {
    survey: 1500,
    welcome: 6000,
    first: 3000,
    second: 6000,
    third: 12000,
  },
  kes: {
    survey: 400,
    welcome: 1600,
    first: 800,
    second: 1600,
    third: 3200,
  },
  ils: {
    survey: 10,
    welcome: 40,
    first: 20,
    second: 40,
    third: 80,
  },
  sar: {
    survey: 10,
    welcome: 40,
    first: 20,
    second: 40,
    third: 80,
  },
  qar: {
    survey: 10,
    welcome: 40,
    first: 20,
    second: 40,
    third: 80,
  },
  bhd: {
    survey: 1,
    welcome: 4,
    first: 2,
    second: 4,
    third: 8,
  },
  kwd: {
    survey: 1,
    welcome: 4,
    first: 2,
    second: 4,
    third: 8,
  },
  omr: {
    survey: 1,
    welcome: 4,
    first: 2,
    second: 4,
    third: 8,
  },
  pkr: {
    survey: 1000,
    welcome: 4000,
    first: 2000,
    second: 4000,
    third: 8000,
  },
  bdt: {
    survey: 500,
    welcome: 2000,
    first: 1000,
    second: 2000,
    third: 4000,
  },
  npr: {
    survey: 400,
    welcome: 1600,
    first: 800,
    second: 1600,
    third: 3200,
  },
  lkr: {
    survey: 1000,
    welcome: 4000,
    first: 2000,
    second: 4000,
    third: 8000,
  },
  mmk: {
    survey: 10000,
    welcome: 40000,
    first: 20000,
    second: 40000,
    third: 80000,
  },
  khr: {
    survey: 12000,
    welcome: 48000,
    first: 24000,
    second: 48000,
    third: 96000,
  },
  lak: {
    survey: 50000,
    welcome: 200000,
    first: 100000,
    second: 200000,
    third: 400000,
  },
  bnd: {
    survey: 4,
    welcome: 16,
    first: 8,
    second: 16,
    third: 32,
  },
  mop: {
    survey: 25,
    welcome: 100,
    first: 50,
    second: 100,
    third: 200,
  },
  mnt: {
    survey: 10000,
    welcome: 40000,
    first: 20000,
    second: 40000,
    third: 80000,
  },
  uzs: {
    survey: 35000,
    welcome: 140000,
    first: 70000,
    second: 140000,
    third: 280000,
  },
  kzt: {
    survey: 1500,
    welcome: 6000,
    first: 3000,
    second: 6000,
    third: 12000,
  },
  kgs: {
    survey: 250,
    welcome: 1000,
    first: 500,
    second: 1000,
    third: 2000,
  },
  tjs: {
    survey: 35,
    welcome: 140,
    first: 70,
    second: 140,
    third: 280,
  },
  tmt: {
    survey: 10,
    welcome: 40,
    first: 20,
    second: 40,
    third: 80,
  },
  afn: {
    survey: 250,
    welcome: 1000,
    first: 500,
    second: 1000,
    third: 2000,
  },
  nok: {
    survey: 30,
    welcome: 150,
    first: 80,
    second: 150,
    third: 300,
  },
  sek: {
    survey: 30,
    welcome: 150,
    first: 80,
    second: 150,
    third: 300,
  },
  dkk: {
    survey: 20,
    welcome: 100,
    first: 50,
    second: 100,
    third: 200,
  },
  pln: {
    survey: 12,
    welcome: 60,
    first: 30,
    second: 60,
    third: 120,
  },
  czk: {
    survey: 70,
    welcome: 350,
    first: 175,
    second: 350,
    third: 700,
  },
  huf: {
    survey: 1000,
    welcome: 5000,
    first: 2500,
    second: 5000,
    third: 10000,
  },
  try: {
    survey: 80,
    welcome: 400,
    first: 200,
    second: 400,
    third: 800,
  },
  uah: {
    survey: 100,
    welcome: 500,
    first: 250,
    second: 500,
    third: 1000,
  },
  isk: {
    survey: 400,
    welcome: 2000,
    first: 1000,
    second: 2000,
    third: 4000,
  },
  ron: {
    survey: 15,
    welcome: 75,
    first: 40,
    second: 75,
    third: 150,
  },
  bgn: {
    survey: 5,
    welcome: 25,
    first: 15,
    second: 25,
    third: 50,
  },
  hrk: {
    survey: 20,
    welcome: 100,
    first: 50,
    second: 100,
    third: 200,
  },
  rsd: {
    survey: 300,
    welcome: 1500,
    first: 750,
    second: 1500,
    third: 3000,
  },
  mad: {
    survey: 30,
    welcome: 150,
    first: 75,
    second: 150,
    third: 300,
  },
  dzd: {
    survey: 400,
    welcome: 2000,
    first: 1000,
    second: 2000,
    third: 4000,
  },
  tnd: {
    survey: 10,
    welcome: 50,
    first: 25,
    second: 50,
    third: 100,
  },
  lyd: {
    survey: 15,
    welcome: 75,
    first: 40,
    second: 75,
    third: 150,
  },
};

export const convertIsoToExtended = (locale) => {
  const isoToExtendedMap = {
    "zh-CN": "zh-Hans-CN", // Simplified Chinese (China)
    "zh-SG": "zh-Hans-CN", // Simplified Chinese (Singapore)
    "zh-MY": "zh-Hans-CN", // Simplified Chinese (Malaysia)
    "zh-TW": "zh-Hant-TW", // Traditional Chinese (Taiwan)
    "zh-HK": "zh-Hant-TW", // Traditional Chinese (Hong Kong)
    "zh-MO": "zh-Hant-TW", // Traditional Chinese (Macau)
    zh: "zh-Hans-CN", // fall back to simple chinese
    en: "en", // English
    id: "en", // Indonesian

    "zh-Hans-CN": "zh-Hans-CN", // Simplified Chinese (China)
    "zh-Hant-TW": "zh-Hant-TW", // Traditional Chinese (Taiwan)
  };

  // If exact match exists, return mapped value
  if (isoToExtendedMap[locale]) {
    return isoToExtendedMap[locale];
  }

  // Try matching just the language code
  const languageCode = locale.split("-")[0];
  if (isoToExtendedMap[languageCode]) {
    return isoToExtendedMap[languageCode];
  }

  // Default to English if no match found
  return "en";
};

export function getReferralAmount(currency, tier, defaultValue) {
  const referralAmount = getReferralAmountWithoutCurrency(currency, tier, defaultValue);

  if (!referralAmount) {
    return undefined;
  }

  return formatCurrency(referralAmount);
}

export function getReferralAmountWithoutCurrency(currency, tier, defaultValue) {
  const referralAmount = currencyToCreditsGiftMap[currency]?.[tier] || defaultValue;

  return referralAmount;
}

function getLanguageFromQuery() {
  const urlParams = new URLSearchParams(window.location.search);
  const lang = urlParams.get("lang");
  return lang ? convertIsoToExtended(lang) : null;
}

// Google and Bing must see each URL in its own language: `?lang=` if present,
// else English (the rule the Astro trip pages follow). Otherwise the crawler's
// IP country picks it — Googlebot also crawls from Taiwan, so Google indexed
// "/" in Traditional Chinese and showed that snippet to English searchers
// (Oct 2026). Google-InspectionTool is Search Console's live test. China's
// search engines (Baidu, Sogou…) keep the IP-based language on purpose.
const SEARCH_CRAWLER_UA = /googlebot|google-inspectiontool|bingbot/i;

function isSearchCrawler() {
  return SEARCH_CRAWLER_UA.test(navigator.userAgent || "");
}

function isUserLoggedIn() {
  // A malformed USER_ID means we have no usable identity — treat as logged-out so
  // we never fetch preferences with a garbage id (which 500s getUserById).
  return !!(getValidUserId() && localStorage.getItem("JWT_TOKEN"));
}

function getCachedLocale() {
  const language = localStorage.getItem("locale");
  const community = localStorage.getItem("community");
  const currency = localStorage.getItem("currency");

  if (language && community && currency) {
    return { language, community, currency };
  }
  return null;
}

async function fetchUserPreferences() {
  try {
    const userId = getValidUserId();
    if (!userId) {
      return { language: null, community: null, currency: null };
    }
    const res = await axiosInstance.get("/v1/user/getUserById", {
      params: { user_id: userId },
    });
    return {
      language: res.data?.preferred_language,
      community: res.data?.community,
      currency: res.data?.currency,
    };
  } catch (err) {
    console.error("Failed to fetch user preferences", err);
    return { language: null, community: null, currency: null };
  }
}

async function fetchLocationBasedLocale() {
  try {
    const response = await axiosInstance.post("/v1/admin/getIpForFrontend");
    const countryCode = response.data?.country_code?.toLowerCase();

    if (!countryCode) {
      return { community: null, currency: null };
    }

    return {
      community: countryCode,
      currency: communityToCurrencyMap[countryCode] || null,
    };
  } catch (err) {
    console.error("Failed to fetch location-based locale", err);
    return { community: null, currency: null };
  }
}

async function computeUserLocale() {
  let language = null;
  let community = null;
  let currency = null;

  try {
    if (isUserLoggedIn()) {
      const userPrefs = await fetchUserPreferences();
      language = userPrefs.language;
      community = userPrefs.community;
      currency = userPrefs.currency;
    }

    if (!community || !currency) {
      const locationData = await fetchLocationBasedLocale();
      community = community || locationData.community;
      currency = currency || locationData.currency;
    }

    if (community && currency) {
      localStorage.setItem("community", community);
      localStorage.setItem("currency", currency);
    }

    const languageFromQuery = getLanguageFromQuery();
    language =
      languageFromQuery ||
      language ||
      (isSearchCrawler() ? "en" : null) ||
      communityToLanguageMap[community] ||
      convertIsoToExtended(navigator.language || navigator.userLanguage) ||
      "en";

    localStorage.setItem("locale", language);

    return {
      language,
      community: community || "sg",
      currency: currency || "sgd",
    };
  } catch (err) {
    console.error("Locale detection failed", err);
    return { language: "en", community: "us", currency: "usd" };
  }
}

// In-memory locale memo + single-flight guard.
//
// detectUserLocale is invoked from every axios request interceptor (axios.js,
// authAxios.js) AND every trip card's useUserLocale mount — dozens of times per
// page load on /all-trips. Previously each invocation independently ran
// computeUserLocale → getUserById + getIpForFrontend with no dedup, producing a
// storm of identical requests (amplified further when a bad id made every
// getUserById 500). The memo resolves the locale AT MOST ONCE per session and
// the single-flight promise collapses concurrent callers onto that one
// computation. The RESOLUTION LOGIC (computeUserLocale) is unchanged — this only
// stops it from re-running redundantly. Invalidated on an explicit locale change
// (invalidateLocaleCache, called from useUserLocale.handleChangeLocale), on 401
// logout (authAxios), and — because the memo is keyed to the identity it was
// resolved for — automatically whenever the identity changes (in-SPA login,
// account switch/merge), so server-side preferences apply without a refresh.
let localeMemo = null;
let localeMemoIdentity = null;
let inFlightLocale = null;
let inFlightIdentity = null;

// Who the locale would be resolved FOR right now: the logged-in user's id, or
// "anon". Login/logout/account-switch all change this key, which is what makes
// a memo resolved under the previous identity stale.
function currentLocaleIdentity() {
  const userId = getValidUserId();
  return userId && localStorage.getItem("JWT_TOKEN") ? userId : "anon";
}

export function invalidateLocaleCache() {
  localeMemo = null;
  localeMemoIdentity = null;
  inFlightLocale = null;
  inFlightIdentity = null;
}

export async function detectUserLocale() {
  const languageFromQuery = getLanguageFromQuery();
  const identity = currentLocaleIdentity();

  // A ?lang= override is authoritative for language and requires no network —
  // apply it on top of whatever base locale we resolve.
  const applyQueryLang = locale => {
    if (languageFromQuery) {
      localStorage.setItem("locale", languageFromQuery);
      return { ...locale, language: languageFromQuery };
    }
    return locale;
  };

  // 1. Session memo — the common case after first resolution. Collapses the
  //    dozens of per-card / per-request calls into a single cached result. Only
  //    honored while it belongs to the current identity: a memo resolved while
  //    logged out must not survive an in-SPA login (and vice versa).
  if (localeMemo && localeMemoIdentity === identity) {
    return applyQueryLang(localeMemo);
  }

  // 2. Logged-out fast path (unchanged): a complete localStorage locale, no
  //    network. Logged-in users still fall through to a fresh computation so
  //    their server-side preferences win — but only ONCE, then memoized.
  const cached = getCachedLocale();
  if (cached && identity === "anon") {
    localeMemo = cached;
    localeMemoIdentity = identity;
    return applyQueryLang(cached);
  }

  // 3. Compute, single-flighted: N concurrent callers share ONE getUserById +
  //    getIpForFrontend round-trip instead of firing a pair each. A flight
  //    started under a different identity is superseded, and its late result is
  //    never memoized over the newer one.
  if (!inFlightLocale || inFlightIdentity !== identity) {
    inFlightIdentity = identity;
    const flight = computeUserLocale()
      .then(result => {
        // Memoize only if this flight is still current — not if it was
        // invalidated or superseded by an identity change while in the air.
        if (inFlightLocale === flight) {
          localeMemo = result;
          localeMemoIdentity = identity;
        }
        return result;
      })
      .finally(() => {
        if (inFlightLocale === flight) {
          inFlightLocale = null;
          inFlightIdentity = null;
        }
      });
    inFlightLocale = flight;
  }
  const computed = await inFlightLocale;
  return applyQueryLang(computed);
}

export const countryToNationality = {
  afghanistan: "Afghan",
  albania: "Albanian",
  algeria: "Algerian",
  argentina: "Argentinian",
  australia: "Australian",
  austria: "Austrian",
  bangladesh: "Bangladeshi",
  belgium: "Belgian",
  brazil: "Brazilian",
  bulgaria: "Bulgarian",
  cambodia: "Cambodian",
  canada: "Canadian",
  chile: "Chilean",
  china: "Chinese",
  colombia: "Colombian",
  croatia: "Croatian",
  "czech republic": "Czech",
  denmark: "Danish",
  egypt: "Egyptian",
  england: "English",
  estonia: "Estonian",
  ethiopia: "Ethiopian",
  finland: "Finnish",
  france: "French",
  germany: "German",
  greece: "Greek",
  "hong kong": "Hong Konger",
  hungary: "Hungarian",
  iceland: "Icelandic",
  india: "Indian",
  indonesia: "Indonesian",
  iran: "Iranian",
  iraq: "Iraqi",
  ireland: "Irish",
  israel: "Israeli",
  italy: "Italian",
  japan: "Japanese",
  kenya: "Kenyan",
  "north korea": "North Korean",
  "south korea": "South Korean",
  korea: "South Korean",
  laos: "Laotian",
  latvia: "Latvian",
  lebanon: "Lebanese",
  lithuania: "Lithuanian",
  luxembourg: "Luxembourger",
  malaysia: "Malaysian",
  mexico: "Mexican",
  mongolia: "Mongolian",
  morocco: "Moroccan",
  nepal: "Nepali",
  netherlands: "Dutch",
  "new zealand": "New Zealander",
  nigeria: "Nigerian",
  norway: "Norwegian",
  pakistan: "Pakistani",
  peru: "Peruvian",
  philippines: "Filipino",
  poland: "Polish",
  portugal: "Portuguese",
  qatar: "Qatari",
  romania: "Romanian",
  russia: "Russian",
  "saudi arabia": "Saudi Arabian",
  scotland: "Scottish",
  serbia: "Serbian",
  singapore: "Singaporean",
  slovakia: "Slovak",
  slovenia: "Slovenian",
  africa: "African",
  "south africa": "South African",
  spain: "Spanish",
  "sri lanka": "Sri Lankan",
  sweden: "Swedish",
  switzerland: "Swiss",
  syria: "Syrian",
  taiwan: "Taiwanese",
  thailand: "Thai",
  tunisia: "Tunisian",
  turkey: "Turkish",
  ukraine: "Ukrainian",
  "united arab emirates": "Emirati",
  "united kingdom": "British",
  "united states": "American",
  uruguay: "Uruguayan",
  venezuela: "Venezuelan",
  vietnam: "Vietnamese",
  wales: "Welsh",
  yemen: "Yemeni",
  zimbabwe: "Zimbabwean",
};
